import React from 'react';
import { FaEnvelope, FaLinkedin, FaPhone, FaRobot, FaDatabase, FaArrowRight, FaChartLine, FaSearch, FaUsersCog, FaPencilAlt, FaFlask, FaRandom, FaReply, FaEnvelopeOpenText, FaBullseye, FaPercentage, FaCogs } from 'react-icons/fa';

const Features: React.FC = () => {
  const services = [
    {
      icon: <FaEnvelope className="text-growlyy-secondary text-4xl mb-4" />,
      title: 'Prospection Mail',
      description: 'Atteignez vos prospects directement dans leur boîte de réception avec des messages personnalisés et des séquences optimisées pour maximiser les taux de réponse.'
    },
    {
      icon: <FaLinkedin className="text-growlyy-secondary text-4xl mb-4" />,
      title: 'Prospection LinkedIn',
      description: 'Exploitez la puissance du réseau professionnel n°1 avec des stratégies d\'outreach ciblées et des messages personnalisés qui génèrent des conversations qualifiées.'
    },
    {
      icon: <FaPhone className="text-growlyy-secondary text-4xl mb-4" />,
      title: 'Prospection Téléphonique',
      description: 'Établissez un contact direct avec vos prospects grâce à nos experts en prospection téléphonique qui qualifient les opportunités et fixent des rendez-vous.'
    }
  ];

  // Étapes du workflow de relance
  const workflowSteps = [
    { icon: <FaEnvelope />, label: 'Premier\nmail', color: 'bg-growlyy-secondary' },
    { icon: <FaEnvelope />, label: 'Relance 1', color: 'bg-growlyy-secondary' },
    { icon: <FaEnvelope />, label: 'Relance 2', color: 'bg-growlyy-secondary' },
    { icon: <FaLinkedin />, label: 'Message\nLinkedIn', color: 'bg-growlyy-primary' },
    { icon: <FaLinkedin />, label: 'Relance 1', color: 'bg-growlyy-primary' },
    { icon: <FaLinkedin />, label: 'Relance 2', color: 'bg-growlyy-primary' },
    { icon: <FaPhone />, label: 'Appel\ntéléphonique', color: 'bg-growlyy-primary-dark' },
    { icon: <FaPhone />, label: 'Relance 1', color: 'bg-growlyy-primary-dark' },
    { icon: <FaPhone />, label: 'Relance 2', color: 'bg-growlyy-primary-dark' }
  ];

  return (
    <section id="fonctionnalites" className="py-20 bg-growlyy-gray-light">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-growlyy-primary mb-4">Prospection Multicanal Avancée</h2>
          <p className="text-xl text-growlyy-gray-dark max-w-2xl mx-auto">
            Découvrez des outils puissants pour optimiser votre prospection sur plusieurs canaux et maximiser votre acquisition de clients.
          </p>
        </div>

        {/* Services de prospection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <div key={index} className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition duration-300 text-center">
              <div className="flex justify-center">
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold text-growlyy-primary mb-3">{service.title}</h3>
              <p className="text-growlyy-gray-dark">{service.description}</p>
            </div>
          ))}
        </div>

        {/* Workflow de relance */}
        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-growlyy-primary mb-10 text-center">Notre workflow de relance multi-canal</h3>
          <div className="relative overflow-hidden">
            <div className="flex justify-center">
              <div className="grid grid-cols-9 gap-2 items-start max-w-6xl">
                {workflowSteps.map((step, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div className="flex items-center mb-4">
                      {index > 0 && (
                        <FaArrowRight className="text-growlyy-gray-dark mr-2" />
                      )}
                      <div className={`${step.color} text-white rounded-full w-16 h-16 flex items-center justify-center`}>
                        {step.icon}
                      </div>
                    </div>
                    <div className="h-12 flex items-center">
                      <p className="text-sm text-growlyy-gray-dark text-center w-24 whitespace-pre-line">
                        {step.label}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mb-16">
          <a href="https://zcal.co/adriengeen/15min" target="_blank" rel="noopener noreferrer" className="btn-primary btn-large">
            Commencer maintenant
          </a>
        </div>

        {/* Nos technologies d'acquisition */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-growlyy-primary mb-4">Nos technologies d'acquisition</h2>
            <p className="text-xl text-growlyy-gray-dark max-w-3xl mx-auto">
              Chez Growlyy, nous combinons l'IA et la data pour maximiser votre génération de leads et garantir des résultats 100% à la performance. Nos technologies de prospection automatisée nous permettent d'identifier, qualifier et convertir vos prospects avec une précision inégalée.
            </p>
          </div>

          {/* Prospection augmentée par IA */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-growlyy-secondary mb-8 text-center">Prospection augmentée par IA</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaChartLine className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Score de conversion intelligent</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Notre IA analyse des milliers de signaux pour classer et prioriser les prospects selon leur probabilité de conversion, afin que chaque contact ait un fort potentiel de closing.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaSearch className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Détection des signaux d'achat</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Growlyy identifie en temps réel les opportunités de prospection en analysant les nouvelles embauches, les levées de fonds, les posts LinkedIn et les tendances du marché, pour contacter les bonnes personnes au bon moment.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaUsersCog className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Segmentation dynamique et ciblage précis</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Fini les bases de données obsolètes. Nos algorithmes actualisent et segmentent en continu vos prospects selon leur secteur, leur taille, leur croissance et leurs actions récentes.
                </p>
              </div>
            </div>
          </div>

          {/* Personnalisation avancée des séquences de prospection */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-growlyy-secondary mb-8 text-center">Personnalisation avancée des séquences de prospection</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaPencilAlt className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Rédaction automatisée et ultra-personnalisée</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Nous générons des messages impactants adaptés aux profils LinkedIn, aux actualités de l'entreprise et aux échanges précédents, garantissant des taux de réponse élevés.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaFlask className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Test A/B intelligent</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Nos séquences évoluent en temps réel : l'IA analyse les taux d'ouverture et de réponse pour optimiser les variantes les plus performantes et maximiser votre ROI.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaRandom className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Scénarios multi-canaux</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Nous synchronisons vos campagnes LinkedIn, email, cold calling et SMS pour créer des parcours prospect fluides et maximiser les points de contact avec vos cibles.
                </p>
              </div>
            </div>
          </div>

          {/* Automatisation des relances et suivi intelligent */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-growlyy-secondary mb-8 text-center">Automatisation des relances et suivi intelligent</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaReply className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Relances dynamiques et adaptatives</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Fini les relances génériques. Notre IA ajuste le ton, le contenu et le timing des messages en fonction des interactions du prospect, pour un impact maximal.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaRobot className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Agent IA de qualification</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Notre assistant intelligent analyse et qualifie les réponses des prospects, détecte leur niveau d'intérêt et recommande la meilleure action suivante.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaEnvelopeOpenText className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Détection et relance des e-mails non lus</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Nous suivons les interactions avec vos e-mails et relançons automatiquement les prospects qui ont ouvert sans répondre, avec des approches optimisées.
                </p>
              </div>
            </div>
          </div>

          {/* Pourquoi Growlyy */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-growlyy-secondary mb-8 text-center">Pourquoi Growlyy ?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaBullseye className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Technologie de pointe pour une prospection ultra-ciblée</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Notre plateforme utilise des algorithmes avancés pour identifier les prospects les plus susceptibles de convertir, vous permettant de concentrer vos efforts sur les opportunités à fort potentiel.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaPercentage className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">100% à la performance : on gagne si vous gagnez</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Notre modèle économique est aligné avec votre succès. Vous ne payez que pour les résultats concrets : des rendez-vous qualifiés qui ont le potentiel de se transformer en clients.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaCogs className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">Optimisation continue grâce à l'IA et l'automatisation</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Notre système apprend et s'améliore en continu, analysant les résultats pour affiner les stratégies et maximiser votre retour sur investissement à chaque étape.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition duration-300">
                <div className="flex justify-center mb-4">
                  <div className="bg-growlyy-secondary rounded-full w-14 h-14 flex items-center justify-center">
                    <FaRobot className="text-white text-2xl" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-growlyy-primary mb-3 text-center">L'alliance de l'IA, de la data et de l'humain</h4>
                <p className="text-growlyy-gray-dark text-center">
                  Growlyy transforme la prospection B2B en combinant la puissance de l'intelligence artificielle et de l'analyse de données avec l'expertise humaine pour des résultats exceptionnels.
                </p>
              </div>
            </div>
          </div>

          {/* Bouton CTA */}
          <div className="text-center mt-12">
            <a href="https://zcal.co/adriengeen/15min" target="_blank" rel="noopener noreferrer" className="btn-primary btn-large btn-pulse">
              Commencer maintenant
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features; 